import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Field, useToast } from '@kollez/design-system';
import { apiPost } from '@/lib/api';
import { PageMeta } from '@/components/PageMeta';

const CONTACT_META = {
  title: 'Contact',
  description: 'Talk to us — book a demo, ask about pricing, or request a walkthrough with your placement officer.',
  canonical: '/contact',
} as const;

/** Contact (F80) — sales lead form. POST /public/leads (rate-limited). */
export function ContactPage() {
  const [params] = useSearchParams();
  const initialTopic = params.get('topic') ?? params.get('plan') ?? 'demo';
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const toast = useToast();

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setBusy(true);
    try {
      await apiPost('/public/leads', {
        name: fd.get('name'),
        role: fd.get('role'),
        email: fd.get('email'),
        org: fd.get('org'),
        phone: fd.get('phone'),
        collegesBand: fd.get('colleges'),
        note: fd.get('note'),
        source: initialTopic,
      });
      setDone(true);
    } catch {
      toast.show({ tone: 'warning', message: 'Could not send. Try emailing sales@kollez.ai directly.' });
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <>
        <PageMeta {...CONTACT_META} />
        <section className="mx-auto max-w-page-wide px-5 py-24 lg:px-10">
          <div className="card card-pad max-w-xl mx-auto grid gap-3 justify-items-start">
            <span className="badge badge-success">Thanks — we've got it.</span>
            <h1 className="h-serif text-3xl">A member of our team will write to you within one working day.</h1>
            <p className="text-ink-secondary">We usually schedule a 30-minute demo and a walkthrough with your placement officer.</p>
            <Link to="/" className="btn btn-outline">Back to home</Link>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageMeta {...CONTACT_META} />
      <section className="mx-auto max-w-page-wide px-5 py-16 lg:px-10 grid gap-8 lg:grid-cols-[1fr_1.2fr]">
      <div>
        <p className="eyebrow">Talk to us</p>
        <h1 className="h-serif text-4xl">Book a demo, or ask us anything.</h1>
        <p className="mt-4 text-ink-secondary max-w-md">
          We'll schedule a 30-minute call with the placement officer or hiring lead — bring one live drive or role and we'll walk through it end-to-end.
        </p>
        <ul className="mt-8 grid gap-2 text-sm text-ink-secondary">
          <li>Sales: <a className="link" href="mailto:sales@kollez.ai">sales@kollez.ai</a></li>
          <li>Support: <a className="link" href="mailto:support@kollez.ai">support@kollez.ai</a></li>
          <li>Onboarding: <a className="link" href="mailto:onboarding@kollez.ai">onboarding@kollez.ai</a></li>
        </ul>
      </div>
      <form onSubmit={submit} className="card card-pad grid gap-4">
        <Field label="Your name" required>
          <input name="name" className="input" required autoComplete="name" />
        </Field>
        <Field label="Your role" required>
          <input name="role" className="input" placeholder="e.g. TPO, Group Placement Head, Talent Acquisition" required autoComplete="organization-title" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email" required>
            <input name="email" type="email" className="input" required autoComplete="email" />
          </Field>
          <Field label="Phone" optional>
            <input name="phone" type="tel" className="input" autoComplete="tel" inputMode="tel" />
          </Field>
        </div>
        <Field label="Institution or company" required>
          <input name="org" className="input" required autoComplete="organization" />
        </Field>
        <Field label="How many colleges?">
          <select name="colleges" className="input select" defaultValue="1">
            <option value="1">1 college</option>
            <option value="2-5">2–5 colleges</option>
            <option value="6-20">6–20 colleges</option>
            <option value="21-50">21–50 colleges</option>
            <option value="50+">50+ colleges</option>
            <option value="company">I'm a company</option>
          </select>
        </Field>
        <Field label="What can we help with?">
          <textarea name="note" rows={4} className="input textarea" defaultValue={initialTopic === 'fraud' ? 'Reporting a suspicious opening or fee-charging listing.' : ''} />
        </Field>
        <button type="submit" className="btn btn-primary btn-lg" disabled={busy}>{busy ? 'Sending…' : 'Send'}</button>
        <p className="helper m-0">By sending this, you consent to us contacting you about your enquiry. We don't share your details.</p>
      </form>
    </section>
    </>
  );
}
